import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  BookOpen,
  FileText,
  BarChart3,
  Settings,
  School,
  UserCheck,
  TrendingUp,
  Shield,
  ChevronRight,
  ChevronDown,
  MoreHorizontal,
  User,
  LogOut,
  MessageSquare,
  Info,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { clearAuthTokens, getPortalType, getUserData, clearAllSessions, isLocalStorageAvailable, getAuthToken } from '@/utils/auth';
import { apiUrl } from '@/api/config';
import axios from 'axios';

const menuItems = [
  { id: 'dashboard', label: 'DASHBOARD', icon: LayoutDashboard, color: 'text-blue-400', type: 'regular' },
  { id: 'classes', label: 'CLASSES', icon: BookOpen, color: 'text-purple-400', type: 'regular' },
  {
    id: 'users',
    label: 'USER MANAGEMENT',
    icon: Users,
    color: 'text-green-400',
    type: 'expandable',
    children: [
      { id: 'students', label: 'STUDENTS', icon: UserCheck, color: 'text-green-300' },
      { id: 'teachers', label: 'TEACHERS', icon: Users, color: 'text-blue-300' },
    ]
  },
  {
    id: 'content',
    label: 'CONTENT OVERSIGHT',
    icon: BookOpen,
    color: 'text-purple-400',
    type: 'expandable',
    children: [
      { id: 'lessons', label: 'LESSONS', icon: BookOpen, color: 'text-purple-300' },
      { id: 'assessments', label: 'ASSESSMENTS', icon: FileText, color: 'text-blue-300' },
    ]
  },
  {
    id: 'analytics',
    label: 'ANALYTICS & REPORTS',
    icon: BarChart3,
    color: 'text-orange-400',
    type: 'expandable',
    children: [
      { id: 'school-analytics', label: 'SCHOOL ANALYTICS', icon: TrendingUp, color: 'text-orange-300' },
      { id: 'student-analytics', label: 'STUDENT ANALYTICS', icon: Users, color: 'text-green-300' },
      { id: 'teacher-analytics', label: 'TEACHER ANALYTICS', icon: UserCheck, color: 'text-blue-300' },
    ]
  },
  { id: 'community', label: 'COMMUNITY', icon: MessageSquare, color: 'text-teal-400', type: 'regular' },
  { id: 'about', label: 'ABOUT', icon: Info, color: 'text-teal-400', type: 'regular' },

];

export default function AdminSidebar({ activeSection, setActiveSection, onNavigate = () => {}, showCloseButton = false, onClose = () => {}, onLogout = () => {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItem, setExpandedItem] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  // Auto-set active section based on URL
  useEffect(() => {
    if (location.pathname.includes('/community')) {
      setActiveSection('community');
    }
  }, [location.pathname, setActiveSection]);

  // Load user profile data
  useEffect(() => {
    const loadUserProfile = () => {
      try {
        // Check if we can safely access window and localStorage
        if (typeof window === 'undefined' || !isLocalStorageAvailable()) {
          // Set default profile if localStorage is not available
          setUserProfile({
            photo: null,
            fullName: 'Admin',
            username: '',
            syllabus: null,
            grade: null,
            school_code: ''
          });
          return;
        }

        let portal;
        try {
          portal = getPortalType(window.location?.pathname || '');
        } catch (e) {
          console.error('Error getting portal type:', e);
          portal = 'admin'; // Default to admin
        }

        let userData;
        try {
          userData = getUserData(portal);
        } catch (e) {
          console.error('Error getting user data:', e);
          userData = null;
        }

        if (userData) {
          let photoUrl = null;
          const photoField = userData.profile_photo || userData.profile_pic_url || userData.photo || userData.avatar;

          if (photoField) {
            if (photoField.startsWith('http://') || photoField.startsWith('https://')) {
              photoUrl = photoField;
            } else {
              const cleanPath = photoField.startsWith('/') ? photoField.slice(1) : photoField;
              photoUrl = apiUrl(cleanPath);
            }
          }

          setUserProfile({
            photo: photoUrl,
            fullName: userData.full_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.username || 'User',
            username: userData.username?.includes('__') ? userData.username.split('__')[1] : (userData.username || ''),
            syllabus: userData.board?.board_name || userData.syllabus || null,
            grade: userData.grade?.grade_name || userData.grade_name || null,
            school_code: userData.school?.school_code || userData.school_code || ''
          });
        } else {
          // Set default profile if no user data
          setUserProfile({
            photo: null,
            fullName: 'Admin',
            username: '',
            syllabus: null,
            grade: null,
            school_code: ''
          });
        }
      } catch (error) {
        console.error('Error loading admin profile:', error);
        // Set default profile if loading fails
        setUserProfile({
          photo: null,
          fullName: 'Admin',
          username: '',
          syllabus: null,
          grade: null,
          school_code: ''
        });
      }
    };

    // Use setTimeout to ensure this runs after render
    const timeoutId = setTimeout(() => {
      loadUserProfile();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  const handleParentClick = (itemId) => {
    setExpandedItem(expandedItem === itemId ? null : itemId);
  };

  const handleChildClick = (childId, parentId) => {
    setActiveSection(childId);
    if (parentId === 'users') {
      if (childId === 'students') navigate('/admin/students');
      else if (childId === 'teachers') navigate('/admin/teachers');
    } else if (parentId === 'content') {
      if (childId === 'lessons') navigate('/admin/lessons');
      else if (childId === 'assessments') navigate('/admin/assessments');
    } else if (parentId === 'analytics') {
      if (childId === 'school-analytics') navigate('/admin/analytics/school');
      else if (childId === 'student-analytics') navigate('/admin/analytics/students');
      else if (childId === 'teacher-analytics') navigate('/admin/analytics/teachers');
    }
    try { onNavigate(); } catch {}
  };

  const handleItemClick = (itemId) => {
    if (itemId === 'dashboard') {
      setActiveSection('dashboard');
      navigate('/admin/dashboard');
    } else if (itemId === 'classes') {
      setActiveSection('classes');
      navigate('/admin/classes');
    } else if (itemId === 'about') {
      setActiveSection('about');
      navigate('/admin/about');
    } else if (itemId === 'community') {
      setActiveSection('community');
      navigate('/admin/community');
    } else {
      setActiveSection(itemId);
    }
      try {
        const item = menuItems.find(m => m.id === itemId);
        if (!item || item.type !== 'expandable') onNavigate();
      } catch {}
  };

  const isChildActive = (item) => {
    if (item.type === 'expandable' && item.children) {
      return item.children.some(child => child.id === activeSection);
    }
    return false;
  };

  return (
    <div className="min-w-fit w-full max-w-[3.5rem] sm:max-w-xs sm:max-w-sm h-[100dvh] bg-[hsl(var(--sidebar-bg))] sm:bg-[hsl(var(--sidebar-bg))] border-r border-[hsl(var(--card-border))] flex flex-col transition-all duration-300 pb-safe">
      {/* Logo + Brand Name */}
      <div className="p-4 sm:p-6 pb-6 sm:pb-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="/logo.png"
            alt="Budlee AI Logo"
            className="h-10 w-10 sm:h-12 sm:w-12 object-contain flex-shrink-0"
          />
          <div className="flex-1 flex flex-col items-start gap-1">
            <img 
              src="/logo_text.png" 
              alt="Budlee AI" 
              className="h-5 sm:h-6 object-contain w-auto" 
            />
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">School Admin</p>
          </div>
        </div>
        {/* Close Button for Mobile */}
        {showCloseButton && (
          <button
            onClick={onClose}
            className="p-1 rounded-full bg-white/90 hover:bg-white shadow z-50 lg:hidden"
            title="Close sidebar"
          >
            <X className="w-4 h-4 text-gray-700" />
          </button>
        )}
      </div>

      {/* Menu Items */}
      <nav className="flex-1 px-3 space-y-1">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isExpanded = expandedItem === item.id;
          const isActive = activeSection === item.id || isChildActive(item);

          return (
            <div key={item.id}>
              {/* Parent Item */}
              <button
                onClick={() => {
                  if (item.type === 'expandable') {
                    handleParentClick(item.id);
                  } else {
                    handleItemClick(item.id);
                  }
                }}
                className={cn(
                  'w-full flex items-center gap-4 px-4 py-3.5 rounded-xl font-medium text-sm uppercase tracking-wide transition-all duration-300 relative overflow-hidden',
                  isActive
                    ? 'bg-[hsl(var(--sidebar-hover))] text-foreground shadow-lg'
                    : 'text-muted-foreground hover:bg-[hsl(var(--sidebar-hover))] hover:text-foreground'
                )}
              >
                {isActive && (
                  <div className="absolute inset-0 rounded-xl p-[2px] bg-gradient-to-r from-[hsl(var(--teal-vivid))] via-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] animate-pulse-glow">
                    <div className="w-full h-full bg-[hsl(var(--sidebar-hover))] rounded-xl" />
                  </div>
                )}

                <div className="relative z-10 flex items-center gap-4 w-full">
                  <Icon className={cn('w-5 h-5 flex-shrink-0', item.color)} />
                  <span className="flex-1 text-left">{item.label}</span>

                  {item.type === 'expandable' && (
                    isExpanded ? (
                      <ChevronDown className="w-4 h-4 transition-transform duration-300" />
                    ) : (
                      <ChevronRight className="w-4 h-4 transition-transform duration-300" />
                    )
                  )}
                </div>
              </button>

              {/* Children Items */}
              {item.type === 'expandable' && item.children && (
                <div
                  className={cn(
                    'overflow-hidden transition-all duration-300 ease-in-out',
                    isExpanded ? 'max-h-96 opacity-100 mt-1' : 'max-h-0 opacity-0'
                  )}
                >
                  <div className="space-y-1 pl-4 pr-0 py-1">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const isChildActiveState = activeSection === child.id;

                      return (
                        <button
                          key={child.id}
                          onClick={() => handleChildClick(child.id, item.id)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm uppercase tracking-wide transition-all duration-300 relative overflow-hidden',
                            isChildActiveState
                              ? 'bg-[hsl(var(--sidebar-hover))] text-foreground shadow-md'
                              : 'text-muted-foreground hover:bg-[hsl(var(--sidebar-hover))]/70 hover:text-foreground'
                          )}
                        >
                          {isChildActiveState && (
                            <div className="absolute inset-0 rounded-lg p-[2px] bg-gradient-to-r from-[hsl(var(--teal-vivid))] via-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] animate-pulse-glow">
                              <div className="w-full h-full bg-[hsl(var(--sidebar-hover))] rounded-lg" />
                            </div>
                          )}

                          <div className="relative z-10 flex items-center gap-3 w-full">
                            <ChildIcon className={cn('w-4 h-4 flex-shrink-0', child.color)} />
                            <span>{child.label}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User Profile Section */}
      {userProfile && (
        <div className="p-3 border-t border-[hsl(var(--card-border))] space-y-3">
          <div className="flex items-center gap-3 px-2">
            <button
              onClick={() => {
                navigate('/admin/profile');
                if (onNavigate) onNavigate();
              }}
              className="w-12 h-12 rounded-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] flex items-center justify-center overflow-hidden flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
              title="View Profile"
            >
              {userProfile.photo ? (
                <img
                  src={userProfile.photo}
                  alt={userProfile.fullName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={cn(
                "w-full h-full items-center justify-center",
                userProfile.photo ? "hidden" : "flex"
              )}>
                <User className="w-6 h-6 text-white" />
              </div>
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate capitalize">{userProfile.username}</p>
              {userProfile.school_code && (
                <p className="text-[11px] text-muted-foreground truncate">{userProfile.school_code}</p>
              )}
              {!userProfile.school_code && userProfile.grade && (
                <p className="text-[11px] text-muted-foreground truncate">{userProfile.grade}</p>
              )}
            </div>
            <button
              onClick={onLogout}
              className={cn(
                'p-2 rounded-lg transition-all duration-300',
                'text-red-400 hover:bg-red-500/10 hover:text-red-300'
              )}
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>

          {(userProfile.syllabus || userProfile.grade) && (
            <div className="px-2">
              <div className="flex items-center gap-2 text-xs">
                {userProfile.syllabus && (
                  <>
                    <span className="text-muted-foreground">Syllabus:</span>
                    <span className="text-foreground font-medium">{userProfile.syllabus}</span>
                  </>
                )}
                {userProfile.syllabus && userProfile.grade && (
                  <span className="text-muted-foreground">|</span>
                )}
                {userProfile.grade && (
                  <>
                    <span className="text-muted-foreground">Grade:</span>
                    <span className="text-foreground font-medium">{userProfile.grade}</span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {!userProfile && (
        <div className="p-3 border-t border-[hsl(var(--card-border))] flex justify-end">
          <button
            onClick={onLogout}
            className={cn(
              'p-2 rounded-lg transition-all duration-300',
              'text-red-400 hover:bg-red-500/10 hover:text-red-300'
            )}
            title="Logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      )}

    </div>
  );
}

